import {
  PROGRAM_AREA_SUGGESTIONS, PROGRAM_CATEGORIES, PROGRAM_LIMITS, PROGRAM_MODES, PROGRAM_PLACES, programLabel,
  type CenterProgram, type ProgramCategory, type ProgramInput, type ProgramMode, type ProgramPlace,
} from '@selfon/domain/browser';
import { useMemo, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth/AuthContext';
import { Alert, Dialog, Empty, errorText, Field, Loading, PageHeader, useLoad } from '../components/ui';
import './programs.css';

/*
 * 관리자 > 프로그램 관리(원장): 센터가 운영하는 프로그램 목록과 등록·수정·중단.
 * 여기 등록한 담당 선생님이 수업 만들기에서 그 프로그램의 담당 선생님 목록이 된다.
 */

const nf = (v: number) => v.toLocaleString('ko-KR');
const EMPTY: ProgramInput = { category: '재활', area: '', name: '', mode: 'INDIVIDUAL', place: 'CENTER', teacherIds: [], prices: [], basePrice: 0, active: true };
const inputOf = (p: CenterProgram): ProgramInput => ({ category: p.category, area: p.area, name: p.name, mode: p.mode, place: p.place, teacherIds: p.teacherIds, prices: p.prices, basePrice: p.basePrice, active: p.active });
/** "55,000 | 50,000" 처럼 적은 가격 → 숫자 목록(쉼표는 천 단위 구분이라 나누지 않는다) */
const parsePrices = (s: string) => s.split(/[|/\s]+/).map((x) => x.replace(/[^\d]/g, '')).filter(Boolean).map(Number);
const pricesText = (prices: readonly number[]) => prices.map(nf).join(' | ');

export function Programs() {
  const { me } = useAuth();
  const centerId = me!.centerId!;
  const q = useLoad(() => Promise.all([api.programs(centerId), api.staff(centerId)]), [centerId]);
  const programs = q.data?.[0].programs ?? [];
  const staff = useMemo(() => (q.data?.[1].staff ?? []).filter((s) => s.active), [q.data]);
  const nameOf = useMemo(() => new Map((q.data?.[1].staff ?? []).map((s) => [s.userId, s.name])), [q.data]);
  const [show, setShow] = useState({ active: true, stopped: false });
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<CenterProgram | 'new' | null>(null);
  const [form, setForm] = useState<ProgramInput>(EMPTY);
  const [priceText, setPriceText] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const fromSessions = useLoad(() => api.importProgramsFromSessions(centerId, true), [centerId]);
  const drafts = fromSessions.data?.drafts ?? [];
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const importDrafts = async () => {
    setImporting(true); setImportError(null);
    try {
      const r = await api.importProgramsFromSessions(centerId, false);
      setNotice(`일정에 쓰던 프로그램 ${r.programs.length}개를 가져왔습니다. 가격이 0원으로 들어갔으니 각 프로그램의 가격을 입력하세요.`);
      q.reload(); fromSessions.reload();
    } catch (e) { setNotice(null); setImportError(errorText(e)); } finally { setImporting(false); }
  };

  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const view = programs.filter((p) => (p.active ? show.active : show.stopped)
    && words.every((w) => `${p.category} ${programLabel(p)} ${p.teacherIds.map((id) => nameOf.get(id) ?? '').join(' ')}`.toLowerCase().includes(w)));

  const open = (p: CenterProgram | 'new') => {
    const f = p === 'new' ? EMPTY : inputOf(p);
    setForm(f); setPriceText(p === 'new' ? '' : pricesText(f.prices)); setEditing(p); setNotice(null);
  };
  const set = <K extends keyof ProgramInput>(k: K, v: ProgramInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  const prices = parsePrices(priceText);
  const base = prices.includes(form.basePrice) ? form.basePrice : prices[0] ?? 0;
  const toggleTeacher = (id: string) => set('teacherIds', form.teacherIds.includes(id) ? form.teacherIds.filter((x) => x !== id) : [...form.teacherIds, id]);

  return (
    <div className="stack programs">
      <PageHeader eyebrow="ADMIN" title="프로그램 관리" sub="센터가 운영하는 치료·상담·평가 프로그램과 담당 선생님, 가격을 관리합니다."
        actions={<button type="button" className="btn btn-primary" onClick={() => open('new')}>+ 프로그램 등록</button>} />
      {notice && <Alert kind="ok">{notice}</Alert>}
      {importError && <Alert>{importError}</Alert>}
      {drafts.length > 0 && (
        <Alert kind="info">
          일정에 쓰인 프로그램 {drafts.length}개({drafts.slice(0, 4).map((d) => d.name).join(', ')}{drafts.length > 4 ? ' 등' : ''})가 아직 이 목록에 없습니다.
          가져오면 새 일정에서 바로 고를 수 있습니다. 이미 만든 일정은 바뀌지 않습니다.{' '}
          <button type="button" className="btn btn-sm btn-primary" disabled={importing} onClick={importDrafts}>{importing ? '가져오는 중…' : '일정에서 가져오기'}</button>
        </Alert>
      )}
      {q.error && <Alert>{q.error}</Alert>}
      {q.loading && !q.data && <Loading />}
      {q.data && <>
        <div className="program-filters">
          <fieldset className="program-use">
            <legend>사용여부</legend>
            <label><input type="checkbox" checked={show.active} onChange={(e) => setShow({ ...show, active: e.target.checked })} /> 정상</label>
            <label><input type="checkbox" checked={show.stopped} onChange={(e) => setShow({ ...show, stopped: e.target.checked })} /> 중단</label>
          </fieldset>
          <label className="program-search"><span>검색</span><input type="search" placeholder="프로그램, 선생님" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
          <span className="muted small" role="status">{view.length}개</span>
        </div>
        {view.length === 0 ? (
          <Empty>{programs.length ? '조건에 맞는 프로그램이 없습니다.' : '아직 등록한 프로그램이 없습니다. “+ 프로그램 등록”으로 시작하세요.'}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table program-table">
              <caption className="sr-only">프로그램 목록</caption>
              <thead><tr><th scope="col">구분</th><th scope="col">프로그램</th><th scope="col">방식</th><th scope="col">장소</th><th scope="col">담당 선생님</th><th scope="col" className="num">가격</th><th scope="col" className="num">기본가격</th><th scope="col">사용여부</th></tr></thead>
              <tbody>
                {view.map((p) => (
                  <tr key={p.id} className={p.active ? '' : 'muted'}>
                    <td>{p.category}</td>
                    <td><button type="button" className="link program-name" onClick={() => open(p)} aria-label={`${programLabel(p)} 수정`}>{programLabel(p)}</button></td>
                    <td>{PROGRAM_MODES[p.mode]}</td>
                    <td>{PROGRAM_PLACES[p.place]}</td>
                    <td>{p.teacherIds.map((id) => nameOf.get(id) ?? '퇴사자').join(', ') || <span className="muted">미지정</span>}</td>
                    <td className="num">{pricesText(p.prices)}원</td>
                    <td className="num">{nf(p.basePrice)}원</td>
                    <td><span className={p.active ? 'program-on' : 'program-off'}>{p.active ? '정상' : '중단'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>}

      <Dialog wide title={editing === 'new' ? '프로그램 등록' : '프로그램 수정'} open={editing !== null} onClose={() => setEditing(null)} submitLabel="저장"
        onSubmit={async () => {
          const body = { ...form, prices, basePrice: base };
          const { program } = editing === 'new' ? await api.createProgram(centerId, body) : await api.updateProgram(centerId, (editing as CenterProgram).id, { ...body, expectedVersion: (editing as CenterProgram).version });
          setNotice(`'${programLabel(program)}' 프로그램을 저장했습니다.`);
          q.reload();
        }}>
        <div className="program-form">
          <Field id="pg-cat" label="구분">
            <select id="pg-cat" value={form.category} onChange={(e) => set('category', e.target.value as ProgramCategory)}>{PROGRAM_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          </Field>
          <Field id="pg-area" label="치료 영역" hint="목록에서 고르거나 직접 적으세요.">
            <input id="pg-area" list="pg-areas" maxLength={PROGRAM_LIMITS.area} placeholder="예: 감각통합" value={form.area} onChange={(e) => set('area', e.target.value)} />
            <datalist id="pg-areas">{PROGRAM_AREA_SUGGESTIONS.map((a) => <option key={a} value={a} />)}</datalist>
          </Field>
          <Field id="pg-name" label={`프로그램 이름 (1~${PROGRAM_LIMITS.name}자)`} hint="예: 발달재활, 일반 - 감통치료, 지역사회 - 아청심리">
            <input id="pg-name" required maxLength={PROGRAM_LIMITS.name} value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field id="pg-mode" label="방식">
            <select id="pg-mode" value={form.mode} onChange={(e) => set('mode', e.target.value as ProgramMode)}>{(Object.keys(PROGRAM_MODES) as ProgramMode[]).map((m) => <option key={m} value={m}>{PROGRAM_MODES[m]}</option>)}</select>
          </Field>
          <Field id="pg-place" label="장소">
            <select id="pg-place" value={form.place} onChange={(e) => set('place', e.target.value as ProgramPlace)}>{(Object.keys(PROGRAM_PLACES) as ProgramPlace[]).map((m) => <option key={m} value={m}>{PROGRAM_PLACES[m]}</option>)}</select>
          </Field>
          <Field id="pg-use" label="사용여부">
            <select id="pg-use" value={form.active ? 'Y' : 'N'} onChange={(e) => set('active', e.target.value === 'Y')}><option value="Y">정상</option><option value="N">중단</option></select>
          </Field>
          <Field id="pg-prices" label="가격(원)" hint={`여러 개면 | 로 나눕니다. 예: 55,000 | 50,000 (최대 ${PROGRAM_LIMITS.prices}개, 무료는 0)`}>
            <input id="pg-prices" required inputMode="numeric" placeholder="55,000 | 50,000" value={priceText} onChange={(e) => setPriceText(e.target.value)} />
          </Field>
          <Field id="pg-base" label="기본가격">
            <select id="pg-base" value={base} disabled={!prices.length} onChange={(e) => set('basePrice', Number(e.target.value))}>
              {prices.length ? prices.map((p) => <option key={p} value={p}>{nf(p)}원</option>) : <option value={0}>가격을 먼저 입력하세요</option>}
            </select>
          </Field>
          <fieldset className="program-teachers">
            <legend>담당 선생님 <span className="muted small">{form.teacherIds.length}명 · 수업 만들기에서 이 프로그램의 담당 선생님으로 나옵니다</span></legend>
            {staff.length === 0 ? <p className="muted small">직원 명부에 활동 중인 선생님이 없습니다.</p> : staff.map((s) => (
              <label key={s.userId}><input type="checkbox" checked={form.teacherIds.includes(s.userId)} onChange={() => toggleTeacher(s.userId)} /> {s.name} <span className="muted small">{s.title}</span></label>
            ))}
          </fieldset>
        </div>
      </Dialog>
    </div>
  );
}
